import React from 'react';
import { FmTabsRoot } from '../FmTabs';

import { FmRoutePage } from '../FmComposition';
import {
  OperationalWorkspaceFilters,
  OperationalWorkspaceHeader,
  OperationalWorkspaceStageNavigation,
} from '../FmOperationalWorkspaceChrome';
import { WorkspaceStageBody } from '../FmOperationalWorkspaceBody';
import { compactGuidance, operationalGuidanceChrome } from '../FmOperationalGuidance.helpers';
import { GuidedCompactHeader, GuidedDockBar, GuidedSummaryStrip } from '../FmOperationalGuidedChrome';
import type {
  FmOperationalDialogDescriptor,
  FmOperationalStageDescriptor,
  FmOperationalWorkspaceDescriptor,
  FmOperationalWorkspaceRootProps,
} from '../FmOperationalWorkspaceRoot.types';
import type { OperationalActionActivate } from '../FmOperationalWorkspaceStateSurfaces';
import { FmResponsive } from '../FmResponsive';
import { OperationalWorkspaceDialogs } from './OperationalWorkspaceDialogs';

interface OperationalWorkspaceFrameProps {
  descriptor: FmOperationalWorkspaceDescriptor;
  presentation: NonNullable<FmOperationalWorkspaceRootProps['presentation']>;
  dataTestId: string;
  activeStageId: string;
  activeStage: FmOperationalStageDescriptor;
  stagesTestId: string;
  bodyTestId: string;
  stageContentRef: React.RefObject<HTMLElement | null>;
  dialogTriggerRef: React.RefObject<HTMLButtonElement | null>;
  tabActivatedStageRef: React.RefObject<string | null>;
  stageScrollRequestRef: React.RefObject<string | null>;
  stageFocusRequestedRef: React.RefObject<boolean>;
  openDialogId: string | null;
  openDialogSnapshot: FmOperationalDialogDescriptor | null;
  setOpenDialog: (id: string | null) => void;
  restoreDialogTrigger: () => void;
  onActivate: OperationalActionActivate;
  inhibited: boolean;
  inhibitionReason: string | null;
}

/**
 * Publishes the sticky chrome dock's height on the workspace scroller as
 * `--fm-operational-dock-h`, so anything scrolled into view (a focused
 * control, a region heading) lands below the dock instead of under it.
 */
function useDockHeight(): React.RefCallback<HTMLDivElement> {
  const observer = React.useRef<ResizeObserver | null>(null);
  return React.useCallback((dock: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    const scroller = dock?.parentElement;
    if (!dock || !scroller) return;
    const publish = () =>
      scroller.style.setProperty('--fm-operational-dock-h', `${dock.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    observer.current = new ResizeObserver(publish);
    observer.current.observe(dock);
  }, []);
}

export function OperationalWorkspaceFrame({
  descriptor,
  presentation,
  dataTestId,
  activeStageId,
  activeStage,
  stagesTestId,
  bodyTestId,
  stageContentRef,
  dialogTriggerRef,
  tabActivatedStageRef,
  stageScrollRequestRef,
  stageFocusRequestedRef,
  openDialogId,
  openDialogSnapshot,
  setOpenDialog,
  restoreDialogTrigger,
  onActivate,
  inhibited,
  inhibitionReason,
}: OperationalWorkspaceFrameProps) {
  const dockRef = useDockHeight();
  const activateStage = (stage: FmOperationalStageDescriptor): void => {
    tabActivatedStageRef.current = stage.id;
    stageScrollRequestRef.current = stage.id;
    descriptor.onEvent?.({ kind: 'stage-change', stageId: stage.id });
  };
  /* The stage rail and the stage body are one Radix tab set. The descriptor owns
     the selected stage, so a tab only asks the route to move; manual activation
     lets arrow keys move focus without leaving the current stage. */
  const onValueChange = (stageId: string): void => {
    const stage = descriptor.stages.find((candidate) => candidate.id === stageId);
    if (stage && stage.status !== 'blocked') activateStage(stage);
  };
  const chrome = operationalGuidanceChrome(descriptor.guidance);
  const compact = compactGuidance(descriptor);
  const onFirstStage = descriptor.stages[0]?.id === activeStageId;
  /* A move from inside the flow (a step's back/forward, a choice's way back)
     lands focus in the new step; only a stepper tab keeps focus on itself. */
  const changeStage = (stageId: string): void => {
    stageFocusRequestedRef.current = true;
    stageScrollRequestRef.current = stageId;
    descriptor.onEvent?.({ kind: 'stage-change', stageId });
  };
  return (
    <FmResponsive>
      {(tier) => (
        <FmTabsRoot asChild value={activeStageId} onValueChange={onValueChange} activationMode="manual">
        <FmRoutePage
          as="section"
          width="flush"
          surface="transparent"
          fit={presentation === 'dialog' ? 'content' : 'shell'}
          layout="column"
          position="relative"
          scroll={presentation === 'dialog' ? 'visible' : 'auto'}
          aria-label={descriptor.ariaLabel}
          className="fm-operational-workspace"
          data-testid={dataTestId}
          data-presentation={presentation}
          data-state={descriptor.state}
          data-tier={tier}
          data-active-stage={activeStageId}
          data-revision={descriptor.revision.id}
          data-chrome={chrome ?? undefined}
        >
          {presentation === 'page' && !compact ? (
            <OperationalWorkspaceHeader descriptor={descriptor} tier={tier} />
          ) : null}
          {presentation === 'page' && compact && onFirstStage ? (
            <GuidedCompactHeader descriptor={descriptor} tier={tier} />
          ) : null}
          <div
            className="fm-operational-workspace__chrome-dock"
            data-chrome={chrome ?? undefined}
            ref={dockRef}
          >
            {compact ? (
              <>
                <GuidedDockBar
                  descriptor={descriptor}
                  activeStageId={activeStageId}
                  stagesTestId={stagesTestId}
                  onStageChange={activateStage}
                />
                <GuidedSummaryStrip
                  guidance={compact}
                  stages={descriptor.stages}
                  activeStageId={activeStageId}
                  tier={tier}
                  testId={dataTestId}
                  onStageChange={changeStage}
                />
              </>
            ) : (
              <OperationalWorkspaceStageNavigation
                descriptor={descriptor}
                activeStageId={activeStageId}
                stagesTestId={stagesTestId}
                onStageChange={activateStage}
              />
            )}
            <OperationalWorkspaceFilters descriptor={descriptor} />
          </div>

          <WorkspaceStageBody
            descriptor={descriptor}
            activeStage={activeStage}
            activeStageId={activeStageId}
            tier={tier}
            dataTestId={dataTestId}
            bodyTestId={bodyTestId}
            stageContentRef={stageContentRef}
            dialogTriggerRef={dialogTriggerRef}
            setOpenDialogId={setOpenDialog}
            onActivate={onActivate}
            onStageChange={changeStage}
            inhibited={inhibited}
            inhibitionReason={inhibitionReason}
          />

          <OperationalWorkspaceDialogs
            descriptor={descriptor}
            openDialogId={openDialogId}
            openDialogSnapshot={openDialogSnapshot}
            inhibited={inhibited}
            setOpenDialogId={setOpenDialog}
            restoreDialogTrigger={restoreDialogTrigger}
          />
        </FmRoutePage>
        </FmTabsRoot>
      )}
    </FmResponsive>
  );
}
